import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/6-best-halloween-events-in-the-uk`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="6 Best Halloween Events in the UK | Win Epic Prizes | DAYMADE"
        description="Celebrate Halloween with 6 of the best Halloween events in the UK"
        openGraph={{
          title:
            '6 Best Halloween Events in the UK | Win Epic Prizes | DAYMADE',
          description:
            'Celebrate Halloween with 6 of the best Halloween events in the UK',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/6-best-halloween-events-in-the-uk',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Halloween party with two skeleton decorations',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'prize,winner,halloween,halloween events in UK,Halloween events London,scarefest alton towers,halloween cinema,Rivoli Ballroom,Muncaster Castle,halloween festival,bongo’s bingo,the ghost bus tours,pumpkin picking,halloween fancy dress,prizes to be won,raffles,weekly draw,experience prizes',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Halloween party with two skeleton decorations"
        heroImageYPosition="45%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            6 of the best Halloween Events in the UK
          </h1>

          <p className="subheading">Get your spooky fever on this Halloween</p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            It’s officially Spooktober, and it’s time to get your Halloween
            festive fever on. Some of you are already organised and have some
            killer fancy dress parties to attend with their outfit already
            planned, or maybe you’re spending it with the family and reliving
            your youth by taking your little one trick or treating! If you’re
            scratching your head and are unsure what to do for your Halloween
            celebrations this year, look no further. Discover the 6 best
            Halloween events in the UK to make you scream, laugh and most
            importantly, show off your ultimate Halloween costume. The UK is
            booming with Halloween events with everything from horror cinema
            experiences in an old school Ballroom to pumpkin carving. There are
            some of the most epic Halloween parties to work those monster mash
            moves, and you can even have a sleepover in a haunted prison. The
            Halloween weekend is jam-packed with all sorts of spooky activities
            to get in the Halloween spirit!
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="Three pumpkins with Halloween carvings"
            className="singleBlogImage"
          />

          <h2>ScareFest at Alton Towers</h2>

          <p>
            This award-winning UK Halloween event is the ultimate family outing
            for all ages. And that’s not all. This year, Scarefest is
            celebrating its 15th Anniversary, so you’re in for a treat (or maybe
            a trick!). Explore the theme park in the darkness to get your thrill
            on with some screams and laughs along the way. You’ll get to ride
            the world’s most immersive rollercoaster, The Wicker Man, through
            the magical woods and explore a mysterious world like nothing you’ve
            seen before. Is that not scary enough? Discover the Alton Tower’s
            Dungeon to interact with the historical characters and try to escape
            the Black Plague - you’ll have a laugh but are guaranteed to be
            spooked out. Don’t want to miss out on Trick or Treating? Scarefest
            has a special town where it’s Trick or Treats every day for their
            visitors, and you’ll get to enjoy some Haribo along the way. Bring
            your friends, little ones, and even the grandparents to experience
            the spookiest Halloween festivals in the UK.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A group of scary fancy-dress actors at Scarefest, Alton Towers"
            className="singleBlogImage"
          />

          <h2>Halloween Movies at London’s Oldest Ballroom</h2>

          <p>
            This event is for all you horror movie buffs. Head down to London’s
            last remaining 1950s ballroom to feel like you’ve travelled back in
            time to enjoy some of the best horror movie classics. The pop-up
            cinema presents a Fright Night cinema experience in an old
            vintage-style setting that will make you feel like having a little
            boogie-woogie. The Rivoli Ballroom is showcasing some of the horror
            movies faves, such as the original Carrie, Ghostbusters,
            Beetlejuice, and many more. Perfect for a date night or evening with
            your friends. Grab your popcorn from their old-school dispenser with
            the traditional boxes. You’ll feel like you’re living in a movie
            whilst rocking out in your scary costume on the edge of your seat.
            You’re guaranteed to have an unforgettable movie night in one of
            London’s most hidden gems. There’s even a Halloween disco you want
            to party into the night. Even better, you could be in with a chance
            to win your{' '}
            <BlogLink internal="/prizes/details/halloween-cinema-experience">
              horror film festival experience
            </BlogLink>{' '}
            for you and your plus one, so all you’ll have to worry about is your
            costume and fight over which popcorn you want.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Pop-up cinema at The Rivoli Ballroom in London"
            className="singleBlogImage"
          />

          <h2>Muncaster Halloween Festival</h2>

          <p>
            Fancy hanging out with zombies, ghosts, and witches this Halloween?
            Well, head over the Muncaster Castle to have the spookiest Halloween
            experience. The castle, located in Cumbria, is not just any
            Halloween event; it’s a full-on day festival! In the daytime, it’s a
            friendly atmosphere for your little ones, but as soon as it turns
            dark… the real horror begins (not recommended for the kids!). Scary
            mazes, zombie shootouts, and even a spooky Halloween disco to jam to
            some classic Ghost Busters. The festival is in a beautiful location
            in Cumbria. Feel truly Halloweeny this October with your family and
            friends if you’re brave enough to venture out in the night.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Kids Wearing Halloween Costumes Standing Near Plants"
            className="singleBlogImage"
          />

          <h2>Pumpkin Picking</h2>

          <p>
            Did you know that York is the most haunted city in the UK? Well
            where is a better place to go pumpkin picking than here? Open from
            the 1st October, you can find your favourite pumpkin to carve faces,
            cats, or even a minion. Perfect for the family or to go with your
            pals. You have until the 31st of October to head over to go pumpkin
            picking and have a real traditional experience. York is jam-packed
            with ghost walks, murder mysteries, and spooky festivals if you are
            still itching to feel scared after your hard day of pumpkin picking.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="A women holding a mini pumpkin"
            className="singleBlogImage"
          />

          <h2>Bongo’s Bingos</h2>

          <p>
            This cracking Halloween event is just for you party people. The epic
            Bongos Bingo has Halloween special week events across the UK with
            tickets still available at Leeds, Edinburgh, and Manchester for a
            spooky immersive experience! Imagine rave meets bingo plus tons of
            epic Halloween fancy dress, dances offs, challenges and sing-along
            tunes then you have Bongos Bingo. Compete to win some fun Halloween
            prizes and, most importantly, their famous Henry hoover prize. A
            super fun night with your group to have a Halloween disco party like
            never before. Tickets usually sell out pretty fast, so don’t miss
            out on your Halloween rave this Spooktober.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="A Bongo’s Bingo event"
            className="singleBlogImage"
          />

          <h2>The Ghost Bus Tours</h2>

          <p>
            How does a sightseeing tour with a spooky and funny twist sound?
            Jump on the award-winning Ghost Bus that’s nothing like your typical
            tour bus. This one is jet-black and full of spooky surprises. You’ll
            get to experience the UK’s only comedy horror sightseeing
            experience, operating all year round but Halloween is their time to
            shine. Get on board from either the most haunted cities of
            Edinburgh, York, or London to see the best attractions and learn
            about the haunted history with your freaky tour guides. Even better,
            you’ll get to immerse yourself in a horror theatre performance on
            wheels to have an unforgettable night full of scares and laughs. A
            hilarious and entertaining day out with your friends or family,
            suitable for all ages.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary7.jpg`}
            alt="The Ghostbus Tour bus in London"
            className="singleBlogImage"
          />

          <h2>Sleepover in a Haunted Prison</h2>

          <p>
            Warning: this Halloween experience isn’t for the scaredy cats. If
            you’re feeling brave and fancy a freaky sleepover, then this one is
            perfect for you. You’ll have the ultimate 12-hour experience staying
            over in the UK’s most haunted prison cell for the night and
            experiencing life behind bars. You’ll have a guided tour around the
            haunted cells and prison grounds before having the evening to
            yourself to do as you please. Play games with your other cellmates,
            explore the cells and even go ghost hunting. If you’re after a
            hair-raising and chilling experience, then grab your overnight bag
            for a night at the spookiest places to stay in the UK.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary8.jpg`}
            alt="A haunted corridor"
            className="singleBlogImage"
          />

          <p>
            The UK is bursting with tons of epic Halloween events for families,
            fancy dress enthusiasts, parties, and scary walks to suit everyone.
            Whatever your tolerance for terror, you can celebrate Halloween and
            make it the spookiest one yet! We’re huge fans of sharing{' '}
            <BlogLink internal="/prizes/experiences">experiences</BlogLink> with
            others, and Halloween is the best time to share a scream, laugh and
            grip each other to hide away from the monsters. You could even win
            some of the best Halloween prizes from luxury hampers,{' '}
            <BlogLink internal="/prizes/details/halloween-doughnut-diy-kits">
              spooky treats
            </BlogLink>
            , freaky experiences, horror movie tickets to a classic{' '}
            <BlogLink internal="/prizes/details/pumpkin-spice-kit">
              pumpkin spiced hot chocolate kit
            </BlogLink>{' '}
            so you can make your own at home. Happy Halloween everyone, and have
            a fang-tastic night.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary9.jpg`}
            alt="Two friends dancing in the woods wearing Halloween costumes"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>Enter now for your chance to win!</p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
